import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, RefreshCw } from "lucide-react";
import { Spinner } from "@/components/site/ProtectedRoute";
import { WorksheetBlocks } from "@/components/avenues/WorksheetBlocks";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function AvenueWorksheetPage() {
  const { slug, wid, userId } = useParams();
  const q = userId ? `?student=${userId}` : "";
  const [w, setW] = useState(null);
  const [a, setA] = useState({});
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState("");
  const ver = useRef(0);
  useSeo({ title: "Worksheet", path: `/learn/${slug}/worksheet/${wid}`, noindex: true });
  const load = () => api.get(`/learn/${slug}/worksheets/${wid}${q}`).then(({ data }) => { setW(data); setA(data.answers || {}); setDirty(false); }).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, [slug, wid, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => {
      const v = ver.current;
      api.put(`/learn/${slug}/worksheets/${wid}${q}`, { answers: a }).then(() => { if (ver.current === v) setDirty(false); }).catch((e) => toast.error(formatApiError(e)));
    }, 1000);
    return () => clearTimeout(t);
  }, [a, dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <p className="text-center py-24 text-rose-600" data-testid="ws-error">{err}</p>;
  if (!w) return <Spinner />;
  const set = (k, v) => { ver.current += 1; setA((x) => ({ ...x, [k]: v })); setDirty(true); };
  const back = userId ? `/host/teach/${userId}/learn/${slug}/${w.part}` : `/learn/${slug}/${w.part}`;
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10 flex flex-col gap-5 print-root" data-testid="avenue-worksheet-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <Link to={back} className="text-sm font-semibold text-[#147C88] inline-flex items-center gap-1" data-testid="ws-back-link"><ArrowLeft className="h-4 w-4" /> Back to Part {w.part}</Link>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="ws-save-status">{dirty ? "Saving..." : "All changes saved"}</span>
          {userId && <button onClick={load} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold" data-testid="ws-refresh-btn"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>}
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B2A4A] text-[#F5ECD7] px-3 py-2 text-xs font-semibold" data-testid="ws-print-btn"><Printer className="h-3.5 w-3.5" /> Print / save as PDF</button>
        </div>
      </div>
      <div>
        <p className="font-mono-ui text-xs tracking-[0.3em] text-[#F08A3C]">AVENUE {String(w.avenue.number).padStart(2, "0")} · {w.avenue.title.toUpperCase()} · FILE {w.number} · PART {w.part}</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-[#0B2A4A]" data-testid="ws-title">{w.title}</h1>
        <p className="mt-2 text-slate-700 leading-relaxed">{w.intro}</p>
        {w.student && <p className="mt-1 text-sm font-semibold text-[#147C88]">Student: {w.student.name}</p>}
        <p className="mt-2 text-xs text-slate-500 no-print">The grey text in each box is an example. Type over it with your own answer. Everything saves automatically. Tap Print to print it or save it to your phone as a PDF.</p>
      </div>
      <WorksheetBlocks blocks={w.blocks} a={a} set={set} />
      <p className="text-xs text-slate-500">Esc Digital Nomad Village · Digital Nomad Nicaragua</p>
    </section>
  );
}
